/**
 * BARS — the supplied standalone visualizer, adapted to the shared call.
 *
 * The reference is `/Users/andrew/Desktop/v2m-development/v2m-visualizer/bars`
 * (`VoiceVisualizer.tsx`): a mirrored frequency spectrum growing out of the centre
 * line, a drifting field of glowing particles behind it, and one round microphone
 * button in the middle of a white stage. That LOOK is the product, so the numbers
 * below are the reference's numbers and the differences are listed rather than
 * quietly introduced.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS THE SAME AS THE REFERENCE, AND WHY EACH NUMBER IS THAT NUMBER
 *
 *   - 64 bars, half of them drawn twice: bar `i` is painted at `centre + i*slot`
 *     and again at its literal reflection, `width - x - slot`. The painted bar is
 *     one pixel narrower than its slot; that single-pixel gutter is what stops 64
 *     bars becoming a solid block.
 *   - Bar height is `byte * 0.5` capped at 250 on the reference's 300-tall stage —
 *     0.425 of the height, which is what `MAX_BAR_FRACTION` says. The gradient runs
 *     over `centre ± barHeight` (TWICE the bar's own extent, so the bar shows the
 *     middle of it) at 80% / 60% / 40% accent, over a 50%-accent shadow.
 *   - 150 particles at 0.2–1.8 px, drifting at ±0.45 px/frame, alpha 0.2–0.7, each
 *     with a 20 px shadow in the accent. They fade to nothing towards the edges
 *     (`fade = 1 - distance/maxDistance`, `maxDistance = min(w,h)/2`), swell with
 *     the spectrum (`1 + byte/512`), and respawn near the centre when they leave.
 *   - The particles drift WHETHER OR NOT anything is being said. In the reference
 *     that falls out of a live microphone: silence is a spectrum of zeroes, and
 *     zeroes still leave the field moving. It is the resting state of the widget,
 *     not an active one, and reproducing it is why this component no longer parks
 *     on a blank stage before the call starts (which read as a broken embed).
 *
 * WHAT IS DELIBERATELY DIFFERENT
 *
 *   1. NO MICROPHONE OF ITS OWN. The reference calls `getUserMedia`, builds an
 *      `AudioContext` and owns an analyser. By the time a widget is live the
 *      platform has all three, so this reads `VoiceControl.spectrum()` and
 *      `levels()` — including the assistant's track while the agent speaks, which
 *      the reference has no concept of. No second permission prompt, no second
 *      capture track, no second audio thread.
 *   2. DEVICE-PIXEL SHARP, REFERENCE-SIZED. The reference draws into a fixed
 *      800×300 bitmap and lets CSS stretch it, so its particles and glow grow with
 *      the container and go soft. This one renders at the device ratio and instead
 *      SCALES the reference's constants by `min(w/800, h/300)` — the same picture
 *      at any size, without the stretch artefacts.
 *   3. TIME-BASED DRIFT. The reference advances particles once per frame, so it
 *      moves twice as fast on a 120 Hz display. Here the step is multiplied by the
 *      frame's own duration against 60 Hz, so the field drifts at one speed on
 *      every machine.
 *   4. IT STOPS WHEN NOBODY IS LOOKING. An animation frame on a stranger's page
 *      must not run while the widget is scrolled away or the tab is in the
 *      background; an `IntersectionObserver` and `visibilitychange` park the loop
 *      and restart it. `prefers-reduced-motion` holds the field still and drops to
 *      12 fps.
 *   5. IT IS A REAL BUTTON, with a phase-dependent accessible name, a focus ring,
 *      and a polite live region beside it.
 *   6. THE STAGE IS CONFIGURABLE, because a widget on somebody's page is not a demo
 *      app: bar count, particle density, glow, size, corner radius, the gap under
 *      the stage, and the three colours (surface, button, icon) are all props. The
 *      defaults ARE the reference.
 *   7. A MUTE CONTROL (2026-09-04), to the right of the call control while a call is
 *      live and faded out otherwise — the reference has one button and no way to
 *      stop sending audio short of hanging up, which the flag and the conversation
 *      bar both offer. It is the same two-colour object as the centre control: the
 *      button surface with the icon colour on it while the microphone is open, the
 *      two swapped while it is muted, so "is it on?" needs no third colour. Live-only
 *      because there is nothing to mute before a call, and a control that cannot be
 *      pressed is clutter on a stage. `allowMute={false}` removes it.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS NOT ONE CLASS NAME IN THIS FILE
 *
 * Same rule as `flag-button.tsx`, for the same reason: this component is bundled
 * into `embed.js` and mounted inside a closed shadow root on a page we do not
 * control. It used Tailwind utilities until 2026-08-24, and the failure mode was
 * exactly what that predicts — on a host page where the sheet did not reach it, the
 * stage lost its aspect ratio and its background, the button lost its circle, its
 * white surface and its shadow (a mic glyph floating on white, with the caption
 * stranded at the bottom of the page), and the hover transition lost its transform
 * and shifted the button up and to the left instead of zooming it.
 *
 * So: every colour, dimension, radius, shadow, transition and the focus ring is an
 * inline style; the glyphs are hand-written SVG rather than an icon package's
 * class-styled components; `prefers-reduced-motion` is a `matchMedia` listener and
 * not a media query; and the inherited typography properties are all restated on
 * the root, because a host page's `letter-spacing` or `text-transform` crosses into
 * a component that does not set them. This file depends on no stylesheet at all.
 *
 * ONE CONSEQUENCE WORTH STATING: sizes here are px and never rem. `rem` resolves
 * against the HOST page's `<html>` font-size even inside a shadow root, and pages
 * that set it to 10px exist.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { isLive, type VoiceControl, type VoicePhase, type VoiceWidgetCommon } from './types'
import { withTakeover } from './stage-text'
import { MicOffGlyph } from './mic-glyphs'

/** The system stack, stated rather than inherited — see the header. */
const FONT_STACK =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif"

/** The reference's stage. Every visual constant below is expressed against it and
 *  scaled by `min(w/REF_W, h/REF_H)`, so the picture survives being resized. */
const REF_W = 800
const REF_H = 300

/**
 * THE STAGE'S ONE COLOUR — spectrum, particles, glow and the control's glyph.
 *
 * The reference visualizer's own is `#00c8ff`, and this shipped as that until
 * 2026-08-25. Violet replaces it by product decision, not by taste: cyan is the
 * colour half the audio-widget category already uses, and this stage is 800 px of a
 * customer's page rather than a 56 px button, so the one thing it is is its colour.
 * `#9600fa` also holds up where the cyan was weakest — thin 3 px bars on a white
 * stage, where a light cyan goes to nothing and a violet stays a line.
 *
 * Nothing else here moved: the particles, the glow radius and the bar geometry are
 * still the reference's, and an operator with `data-accent` on their tag is
 * unaffected.
 */
const DEFAULT_ACCENT = '#9600fa'
const DEFAULT_SURFACE = '#ffffff'
const DEFAULT_BUTTON = '#ffffff'
/** Slate 500 — legible on a white page and on a mid-dark one, which is as much as
 *  a default can promise for type that sits on somebody else's background. */
const DEFAULT_CAPTION_COLOR = '#64748b'
const DEFAULT_BARS = 64
const DEFAULT_PARTICLES = 150
const DEFAULT_GLOW = 20
const DEFAULT_BUTTON_SIZE = 56
const DEFAULT_CAPTION_GAP = 12
const DEFAULT_RADIUS = 16

/** `byte * 0.5`, capped at 250 on a 300-tall stage. Read the reference's cap as a
 *  fraction of the height and it survives a stage of any height. */
const MAX_BAR_FRACTION = 0.425
/** Enough for any analyser this platform exposes (256-point transform → 128 bins). */
const SPECTRUM_SIZE = 512
/** The reference's `particleSpeed`, in px per 60 Hz frame at reference scale. */
const DRIFT = 0.9
/** Reduced motion: still field, and a slow repaint rather than a dead canvas. */
const REDUCED_FPS = 12

interface Particle {
  x: number
  y: number
  /** Radius and velocity at REFERENCE scale; multiplied by the stage scale when
   *  drawn, so a resize changes the picture's size and not its composition. */
  r0: number
  vx0: number
  vy0: number
  alpha: number
}

export interface BarsVisualizerProps extends VoiceWidgetCommon {
  control: VoiceControl
  /**
   * Bars across the whole stage, mirrored — so this is rounded to an even number
   * and half of it is what actually gets computed. The reference's 64 is the
   * default; 16 reads as a chunky equaliser and 128 as a fine comb.
   */
  barCount?: number
  /** Particles at reference size. The real count scales with the stage's area, so
   *  the density is what this number means. */
  particles?: number
  /** `shadowBlur` on both the bars and the particles — the reference's `glowLevel`. */
  glow?: number
  /** The stage's width at most. It shrinks with the page and keeps its proportion. */
  width?: number
  /** The stage's height at full width; the same proportion is kept when narrower. */
  height?: number
  /** Diameter of the round call control at the centre. */
  buttonSize?: number
  /** The gap between the stage and the caption under it. */
  captionGap?: number
  /** The stage's corner radius. */
  radius?: number
  /** The stage's surface. Any CSS colour, including `transparent` for a widget that
   *  sits directly on the page's own background. */
  background?: string
  /** The call control's own surface — white on the reference's white stage, which
   *  is only visible because of the shadow under it. */
  buttonColor?: string
  /** The mic/stop glyph. Follows `accent` when unset, as the reference's does. */
  iconColor?: string
  /** The caption under the stage. A NEUTRAL default rather than the accent: the
   *  caption sits outside the stage, on the host page's own background, and
   *  small accent-coloured type on white failed every contrast check it was put
   *  through. */
  captionColor?: string
  /** The mute toggle beside the call control, shown while a call is live. Default true. */
  allowMute?: boolean
  className?: string
}

/** Everything the animation loop reads, refreshed on every render. The loop itself
 *  is created once and never restarted — see the effect. */
interface Params {
  accent: string
  barCount: number
  particles: number
  glow: number
}

export function BarsVisualizer({
  control,
  label = 'Talk to us',
  /** The stop control's accessible name. Not offered in the config editor since
   *  2026-08-25 — nothing on the stage paints it — so this default stands unless a
   *  hand-written tag carries `data-end-label`. */
  endLabel = 'End call',
  accent = DEFAULT_ACCENT,
  caption,
  barCount,
  particles,
  glow,
  width,
  height,
  buttonSize,
  captionGap,
  radius,
  background = DEFAULT_SURFACE,
  buttonColor = DEFAULT_BUTTON,
  iconColor,
  captionColor = DEFAULT_CAPTION_COLOR,
  allowMute = true,
}: BarsVisualizerProps) {
  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const live = isLive(control.phase)
  const waiting = control.phase === 'connecting'

  const bars = evenIn(barCount ?? DEFAULT_BARS, 8, 160)
  const stageW = clamp(width ?? REF_W, 160, 2400)
  const stageH = clamp(height ?? REF_H, 80, 1200)
  const buttonPx = clamp(buttonSize ?? DEFAULT_BUTTON_SIZE, 28, 160)
  const gap = clamp(captionGap ?? DEFAULT_CAPTION_GAP, 0, 96)
  const corner = clamp(radius ?? DEFAULT_RADIUS, 0, 400)
  const ink = iconColor === undefined || iconColor === '' ? accent : iconColor

  /**
   * THE LOOP READS REFS, NOT PROPS, and that is the whole reason it can run for the
   * component's lifetime. `useVoiceControl` memoises on the phase, so `control` is a
   * new object every time the call changes state; an effect keyed on it would tear
   * the canvas down and rebuild the particle field at every phase change, which
   * looks like the field teleporting. Written during render on purpose: the frame
   * that runs before the commit must already see the new values.
   */
  const controlRef = useRef(control)
  controlRef.current = control
  const paramsRef = useRef<Params>({ accent, barCount: bars, particles: 0, glow: 0 })
  paramsRef.current = {
    accent,
    barCount: bars,
    particles: clamp(particles ?? DEFAULT_PARTICLES, 0, 600),
    glow: clamp(glow ?? DEFAULT_GLOW, 0, 80),
  }

  /** Hover/press/focus, in state rather than in CSS — there is no CSS. */
  const [hover, setHover] = useState(false)
  const [pressed, setPressed] = useState(false)
  const [ring, setRing] = useState(false)
  const [muteHover, setMuteHover] = useState(false)
  const [muteRing, setMuteRing] = useState(false)
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const query = matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduced(query.matches)
    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const stage = stageRef.current
    const canvas = canvasRef.current
    if (!stage || !canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const spectrum = new Uint8Array(SPECTRUM_SIZE)
    let dust: Particle[] = []
    let w = 0
    let h = 0
    let scale = 1
    let rgb: readonly [number, number, number] = [0, 200, 255]
    let lastAccent = ''
    let frame = 0
    let lastMs = 0
    let disposed = false
    let onScreen = true
    const reducedQuery = matchMedia('(prefers-reduced-motion: reduce)')

    const sizeToBox = () => {
      const nextW = Math.max(1, stage.clientWidth)
      const nextH = Math.max(1, stage.clientHeight)
      const dpr = Math.min(2, window.devicePixelRatio || 1)
      const bitmapW = Math.round(nextW * dpr)
      const bitmapH = Math.round(nextH * dpr)
      if (nextW === w && nextH === h && canvas.width === bitmapW && canvas.height === bitmapH) return
      w = nextW
      h = nextH
      canvas.width = bitmapW
      canvas.height = bitmapH
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      // The reference's constants are for an 800×300 stage; everything drawn is
      // multiplied by this. The floor keeps a small embed's particles visible.
      scale = clamp(Math.min(w / REF_W, h / REF_H), 0.45, 3)
    }

    /** Density, not a count: `particles` means "this many on a reference stage". */
    const syncDust = (density: number) => {
      const want = clamp(Math.round((density * w * h) / (REF_W * REF_H)), 0, 900)
      while (dust.length > want) dust.pop()
      while (dust.length < want) dust.push(spawn(w, h))
    }

    /**
     * THE LOOP RE-ARMS BEFORE IT DRAWS, and never parks itself.
     *
     * The first version stopped the loop when the tab was hidden or the widget was
     * scrolled away, and restarted it from `visibilitychange` and an observer
     * callback. It deadlocked exactly as that design invites: a page loaded in a
     * BACKGROUND tab got an intersection callback saying "not intersecting", parked
     * before its first frame, and never received a second callback when the tab came
     * forward — so the widget sat there as an empty stage with a button on it. Found
     * by loading this in a hidden tab, 2026-08-24.
     *
     * There was no need for any of it. `requestAnimationFrame` ALREADY stops being
     * called in a background tab; that is the platform's job, not ours. The only gap
     * it leaves is a widget scrolled out of view in a foreground tab, and that costs
     * one early return per frame rather than a state machine that can get stuck.
     */
    const draw = (now: number) => {
      if (disposed) return
      frame = requestAnimationFrame(draw)
      if (!onScreen) {
        // No time step across a gap nobody watched, so the field does not jump when
        // it scrolls back into view.
        lastMs = 0
        return
      }

      const still = reducedQuery.matches
      const minFrameMs = still ? 1000 / REDUCED_FPS : 0
      if (lastMs !== 0 && now - lastMs < minFrameMs) return
      // Against 60 Hz, capped so a backgrounded tab's first frame back does not
      // teleport the field across the stage.
      const step = lastMs === 0 ? 1 : Math.min(3, (now - lastMs) / 16.6667)
      lastMs = now

      const p = paramsRef.current
      const c = controlRef.current
      sizeToBox()
      syncDust(p.particles)
      if (p.accent !== lastAccent) {
        lastAccent = p.accent
        // The stage carries `color: accent`, so the browser resolves any CSS colour
        // — a name, `var(--brand)`, `oklch(...)` — to rgb for us. Cached, because
        // getComputedStyle in an animation frame is a layout read.
        rgb = resolvedRgb(stage)
      }

      // Transparent canvas over the stage's own background: the surface is a CSS
      // colour on the element, so `transparent` and gradients both work and the
      // canvas never has to know what it is sitting on.
      ctx.clearRect(0, 0, w, h)

      const phase = c.phase
      const audible = phase === 'connecting' || phase === 'listening' || phase === 'thinking'
        || phase === 'speaking'
      let bins = 0
      if (audible) {
        const primary = phase === 'speaking' ? 'assistant' : 'user'
        const secondary = primary === 'user' ? 'assistant' : 'user'
        bins = c.spectrum?.(primary, spectrum) ?? 0
        if (bins === 0) bins = c.spectrum?.(secondary, spectrum) ?? 0
      }
      const levels = audible ? c.levels() : { user: 0, assistant: 0 }
      const level = Math.max(levels.user, levels.assistant)

      drawDust(ctx, dust, spectrum, bins, w, h, scale, rgb, p.glow, still ? 0 : step)
      drawBars(ctx, spectrum, bins, level, phase, now, w, h, scale, rgb, p.glow, p.barCount)
    }

    // The only reason the loop skips work: scrolled out of view in a tab somebody is
    // looking at. `isIntersecting` is read as a hint that can only pause drawing —
    // it can no longer stop the loop, so a callback that never arrives costs nothing.
    const watcher = typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver((entries) => {
        onScreen = entries.some((e) => e.isIntersecting)
      })
    watcher?.observe(stage)
    frame = requestAnimationFrame(draw)

    return () => {
      disposed = true
      cancelAnimationFrame(frame)
      watcher?.disconnect()
    }
  }, [])

  const glyph = Math.round(buttonPx * 0.42)
  const muted = control.muted
  /** 0.6 of the call control: clearly the lesser of the two, still a 40 px target at
   *  the default size. Its centre sits one radius, a gap of 0.22 of the control's
   *  side, and its own radius to the right of the stage's centre line. */
  const mutePx = Math.round(clamp(buttonPx * 0.6, 28, 72))
  const muteGlyph = Math.round(mutePx * 0.5)
  const muteOffset = Math.round(buttonPx / 2 + buttonPx * 0.22 + mutePx / 2)
  /** Zoom, not a nudge: the translate that centres the button is on the same
   *  transform as the scale, and scaling about the centre leaves the centre where
   *  it is. This is the bug the Tailwind version had — see the header. */
  const zoom = pressed ? 0.94 : hover ? 1.07 : 1

  const buttonStyle: CSSProperties = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: `translate(-50%, -50%) scale(${zoom})`,
    transformOrigin: 'center',
    transition: reduced ? 'none' : 'transform 240ms cubic-bezier(0.34, 1.4, 0.64, 1), box-shadow 240ms ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: buttonPx,
    height: buttonPx,
    padding: 0,
    margin: 0,
    border: 'none',
    borderRadius: '50%',
    background: buttonColor,
    color: ink,
    cursor: waiting ? 'wait' : 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    fontFamily: FONT_STACK,
    lineHeight: 0,
    outline: 'none',
    // Two shadows and a hairline ring, because the reference's button is white on
    // white and the shadow is the ONLY thing that makes it an object. Without it
    // the widget reads as a stray icon, which is what a customer reported.
    boxShadow: ring
      ? `0 0 0 3px ${DEFAULT_SURFACE}, 0 0 0 5px ${ink}, 0 12px 30px rgba(15, 23, 42, 0.22)`
      : hover
        ? '0 14px 34px rgba(15, 23, 42, 0.26), 0 3px 8px rgba(15, 23, 42, 0.14), inset 0 0 0 1px rgba(15, 23, 42, 0.05)'
        : '0 10px 26px rgba(15, 23, 42, 0.20), 0 2px 6px rgba(15, 23, 42, 0.12), inset 0 0 0 1px rgba(15, 23, 42, 0.05)',
    opacity: waiting ? 0.92 : 1,
  }

  /**
   * The mute toggle's style, built like the centre control's: the same combined
   * translate-and-scale transform (see the header for why they must be one), the
   * same shadow family a size down, and the two colours swapped while muted. It is
   * always in the tree when `allowMute` is set, and fades rather than mounting, so a
   * call going live does not pop a button into existence beside the one just
   * pressed. Not focusable and not hit-testable while hidden.
   */
  const muteStyle: CSSProperties = {
    position: 'absolute',
    left: `calc(50% + ${muteOffset}px)`,
    top: '50%',
    transform: `translate(-50%, -50%) scale(${muteHover ? 1.07 : 1})`,
    transformOrigin: 'center',
    transition: reduced
      ? 'none'
      : 'transform 240ms cubic-bezier(0.34, 1.4, 0.64, 1), opacity 200ms ease, background 150ms ease, color 150ms ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: mutePx,
    height: mutePx,
    padding: 0,
    margin: 0,
    border: 'none',
    borderRadius: '50%',
    background: muted ? ink : buttonColor,
    color: muted ? buttonColor : ink,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    fontFamily: FONT_STACK,
    lineHeight: 0,
    outline: 'none',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    boxShadow: muteRing
      ? `0 0 0 3px ${DEFAULT_SURFACE}, 0 0 0 5px ${ink}, 0 8px 20px rgba(15, 23, 42, 0.2)`
      : '0 8px 20px rgba(15, 23, 42, 0.18), 0 2px 5px rgba(15, 23, 42, 0.12), inset 0 0 0 1px rgba(15, 23, 42, 0.05)',
    opacity: live ? 1 : 0,
    pointerEvents: live ? 'auto' : 'none',
  }

  return (
    <div
      style={{
        // Every inherited property the host page could push in, restated.
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap,
        width: '100%',
        maxWidth: stageW,
        margin: '0 auto',
        padding: 0,
        fontFamily: FONT_STACK,
        fontSize: '12px',
        fontWeight: 400,
        fontStyle: 'normal',
        lineHeight: 1.3,
        letterSpacing: 'normal',
        wordSpacing: 'normal',
        textAlign: 'center',
        textTransform: 'none',
        textIndent: 0,
        direction: 'ltr',
      }}
    >
      <div
        ref={stageRef}
        style={{
          position: 'relative',
          boxSizing: 'border-box',
          width: '100%',
          maxWidth: stageW,
          // Proportion rather than a fixed height, so a 300 px stage on an 800 px
          // page is a 150 px stage on a 400 px one instead of a squashed one.
          aspectRatio: `${stageW} / ${stageH}`,
          overflow: 'hidden',
          borderRadius: corner,
          background,
          // Read back by the canvas to resolve the accent — see the loop.
          color: accent,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', left: 0, top: 0, display: 'block', width: '100%', height: '100%' }}
          aria-hidden
        />
        <button
          type="button"
          onClick={live ? control.stop : control.start}
          disabled={waiting}
          aria-label={live ? endLabel : waiting ? 'Connecting, please wait' : label}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => {
            setHover(false)
            setPressed(false)
          }}
          onMouseDown={() => setPressed(true)}
          onMouseUp={() => setPressed(false)}
          // No stylesheet means no `:focus-visible` rule, so the ring is decided
          // here — the same trick the flag uses.
          onFocus={(e) => setRing(e.currentTarget.matches(':focus-visible'))}
          onBlur={() => setRing(false)}
          style={buttonStyle}
        >
          {waiting ? <SpinnerGlyph size={glyph} /> : live ? <StopGlyph size={glyph} /> : <MicGlyph size={glyph} />}
        </button>
        {allowMute && (
          <button
            type="button"
            onClick={control.toggleMute}
            tabIndex={live ? 0 : -1}
            aria-hidden={!live}
            title={muted ? 'Unmute your microphone' : 'Mute your microphone'}
            aria-label={muted ? 'Unmute your microphone' : 'Mute your microphone'}
            aria-pressed={muted}
            onMouseEnter={() => setMuteHover(true)}
            onMouseLeave={() => setMuteHover(false)}
            onFocus={(e) => setMuteRing(e.currentTarget.matches(':focus-visible'))}
            onBlur={() => setMuteRing(false)}
            style={muteStyle}
          >
            {muted ? <MicOffGlyph size={muteGlyph} /> : <MicGlyph size={muteGlyph} />}
          </button>
        )}
      </div>
      {/*
        THE FAILURE MESSAGE SITS UNDER THE STAGE, NOT ON IT, and it paints its own
        background.

        Inside the stage it collided with the centre button the moment the stage got
        short — at 375 px wide a 300-tall stage is 117 tall, and a two-line message
        pinned to its bottom edge ran straight through the mic (seen on a phone
        viewport, 2026-08-24). Under the stage it cannot collide with anything. The
        red-on-pink chip is self-contained on purpose: red text alone would be
        illegible on a dark host page, and this component cannot see the page.
      */}
      {control.phase === 'error' && control.error ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: '6px 12px',
            borderRadius: 8,
            background: '#fee2e2',
            color: '#b91c1c',
            fontFamily: FONT_STACK,
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: 1.35,
            maxWidth: '100%',
          }}
        >
          {control.error}
        </p>
      ) : null}
      {caption ? (
        <p
          style={{
            margin: 0,
            fontFamily: FONT_STACK,
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.3,
            color: captionColor,
            maxWidth: '100%',
          }}
        >
          {caption}
        </p>
      ) : null}
      <span
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {withTakeover(control, statusSentence(control.phase, control.error))}
      </span>
    </div>
  )
}

// ============================================================================
// The picture
// ============================================================================

function spawn(width: number, height: number): Particle {
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    // The reference's particleSize 0.2–1.8 and particleSpeed 0.9.
    r0: 0.2 + Math.random() * 1.6,
    vx0: (Math.random() - 0.5) * DRIFT,
    vy0: (Math.random() - 0.5) * DRIFT,
    alpha: 0.2 + Math.random() * 0.5,
  }
}

function drawDust(
  ctx: CanvasRenderingContext2D,
  dust: Particle[],
  spectrum: Uint8Array,
  bins: number,
  width: number,
  height: number,
  scale: number,
  rgb: readonly number[],
  glow: number,
  step: number,
): void {
  if (dust.length === 0) return
  const maxDistance = Math.max(1, Math.min(width, height) * 0.5)
  const blur = glow * scale
  for (let i = 0; i < dust.length; i++) {
    const particle = dust[i]!
    particle.x += particle.vx0 * scale * step
    particle.y += particle.vy0 * scale * step
    const fade = Math.max(0, 1 - Math.hypot(width / 2 - particle.x, height / 2 - particle.y) / maxDistance)
    // The reference reads one bin per particle across the WHOLE spectrum, so the
    // field responds to consonants and vowels differently. `1 + byte/512` is its
    // swell, which tops out at 1.5.
    const byte = bins > 0 ? spectrum[Math.floor((i / dust.length) * bins)]! : 0
    const swell = 1 + byte / 512
    ctx.shadowColor = rgba(rgb, fade)
    ctx.shadowBlur = blur
    ctx.fillStyle = rgba(rgb, particle.alpha * swell * fade)
    ctx.beginPath()
    ctx.arc(particle.x, particle.y, particle.r0 * scale * swell, 0, Math.PI * 2)
    ctx.fill()
    ctx.shadowBlur = 0
    if (particle.x < 0 || particle.x > width || particle.y < 0 || particle.y > height) {
      // Back near the middle, the reference's own recycling — which is what keeps
      // the field densest at the centre where the bars are.
      particle.x = width / 2 + (Math.random() - 0.5) * Math.min(100 * scale, width / 2)
      particle.y = height / 2 + (Math.random() - 0.5) * Math.min(100 * scale, height / 2)
    }
  }
}

function drawBars(
  ctx: CanvasRenderingContext2D,
  spectrum: Uint8Array,
  bins: number,
  level: number,
  phase: VoicePhase,
  now: number,
  width: number,
  height: number,
  scale: number,
  rgb: readonly number[],
  glow: number,
  barCount: number,
): void {
  const half = barCount / 2
  const slot = width / barCount
  // One pixel narrower than its slot — the reference's gutter, which is what gives
  // 64 bars definition instead of one solid block.
  const barWidth = Math.max(1, slot - 1)
  const maxHeight = height * MAX_BAR_FRACTION
  /**
   * The bins each bar answers to.
   *
   * The reference runs a 128-point transform and reads bins 0..31 for its 32
   * half-bars: one bin each, low frequencies at the centre, highs at the edges,
   * over the lower half of its spectrum. The shared analyser here is 256-point
   * (128 bins), and the bar count is configurable, so the same RULE is expressed as
   * a ratio — the lower half of whatever the analyser gives, spread across however
   * many bars there are, taking the loudest bin in each bar's span. At 64 bars on a
   * 128-point transform that is exactly the reference's one-bin-per-bar mapping.
   */
  const usable = bins > 0 ? Math.max(1, Math.floor(bins / 2)) : 0
  // Connecting and thinking have nothing to meter but are not idle either; a small
  // breathing floor is what stops those stretches reading as a dead widget.
  const phaseBase = phase === 'connecting'
    ? 0.09 + Math.sin(now / 260) * 0.035
    : phase === 'thinking'
      ? 0.12 + Math.sin(now / 340) * 0.025
      : 0

  ctx.shadowColor = rgba(rgb, 0.5)
  ctx.shadowBlur = glow * scale

  for (let i = 0; i < half; i++) {
    let value = 0
    if (usable > 0) {
      const from = Math.floor((i * usable) / half)
      const until = Math.max(from + 1, Math.floor(((i + 1) * usable) / half))
      for (let b = from; b < until && b < bins; b++) {
        const byte = spectrum[b] ?? 0
        if (byte > value) value = byte
      }
      value /= 255
    }
    // With no analyser (a hand-made control, a fallback recorder) the scalar level
    // drives a symmetrical shape instead, so connecting and thinking still read.
    const fallbackShape = 0.35 + 0.65 * Math.abs(Math.sin(i * 0.47 + now / 190))
    const fallback = Math.max(level, phaseBase) * fallbackShape
    const barHeight = Math.max(scale, Math.max(value, fallback) * maxHeight)
    const y = height / 2 - barHeight / 2
    // The gradient spans centre ± barHeight — TWICE the bar — so the bar shows only
    // the middle of it, exactly as the reference does.
    const gradient = ctx.createLinearGradient(0, height / 2 - barHeight, 0, height / 2 + barHeight)
    gradient.addColorStop(0, rgba(rgb, 0.8))
    gradient.addColorStop(0.5, rgba(rgb, 0.6))
    gradient.addColorStop(1, rgba(rgb, 0.4))
    ctx.fillStyle = gradient

    const rightX = width / 2 + i * slot
    // A literal reflection about x = width/2. Moving the one-pixel gutter to the
    // inner edge instead was the subtle asymmetry in the first port.
    const leftX = width - rightX - slot
    const r = Math.min(2 * scale, barWidth / 2, barHeight / 2)
    roundedRect(ctx, rightX, y, barWidth, barHeight, r)
    roundedRect(ctx, leftX, y, barWidth, barHeight, r)
  }
  ctx.shadowBlur = 0
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath()
  ctx.roundRect(x, y, width, height, radius)
  ctx.fill()
}

function resolvedRgb(element: HTMLElement): readonly [number, number, number] {
  const values = getComputedStyle(element).color.match(/[\d.]+/g)?.map(Number)
  return values && values.length >= 3
    ? [values[0]!, values[1]!, values[2]!]
    : [0, 200, 255]
}

function rgba(rgb: readonly number[], alpha: number): string {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${Math.max(0, Math.min(1, alpha))})`
}

// ============================================================================
// Glyphs — hand-written rather than imported, because an icon package's components
// are styled with class names and this file has none. See the header.
// ============================================================================

function MicGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden focusable="false">
      <path d="M12 2.75a3.25 3.25 0 0 1 3.25 3.25v6a3.25 3.25 0 0 1-6.5 0V6A3.25 3.25 0 0 1 12 2.75Z" />
      <path d="M5.75 10.25a1 1 0 0 1 1 1 5.25 5.25 0 0 0 10.5 0 1 1 0 1 1 2 0 7.25 7.25 0 0 1-6.25 7.18v2.32a1 1 0 1 1-2 0v-2.32a7.25 7.25 0 0 1-6.25-7.18 1 1 0 0 1 1-1Z" />
    </svg>
  )
}

function StopGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden focusable="false">
      <rect x="6.5" y="6.5" width="11" height="11" rx="2.75" />
    </svg>
  )
}

/**
 * The connecting spinner, rotated by the Web Animations API rather than a
 * `@keyframes` rule — there is no stylesheet to put one in. A browser without
 * `element.animate` simply shows a static arc, which is not a failure.
 */
function SpinnerGlyph({ size }: { size: number }) {
  const ref = useRef<SVGSVGElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof el.animate !== 'function') return
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const spin = el.animate(
      [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
      { duration: 900, iterations: Infinity, easing: 'linear' },
    )
    return () => spin.cancel()
  }, [])
  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" strokeOpacity={0.25} />
      <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
    </svg>
  )
}

// ============================================================================
// Small helpers
// ============================================================================

function clamp(n: number, low: number, high: number): number {
  if (!Number.isFinite(n)) return low
  return Math.min(high, Math.max(low, n))
}

/** Bars are mirrored, so an odd count would leave the two halves unequal. */
function evenIn(n: number, low: number, high: number): number {
  const rounded = Math.round(clamp(n, low, high) / 2) * 2
  return clamp(rounded, low, high)
}

function statusSentence(phase: VoicePhase, error: string | null): string {
  switch (phase) {
    case 'idle': return ''
    case 'connecting': return 'Connecting.'
    case 'listening': return 'Connected. Listening.'
    case 'thinking': return 'Connected. Thinking.'
    case 'speaking': return 'Connected. Speaking.'
    case 'ended': return 'Call ended.'
    case 'error': return error ?? 'The call could not be connected.'
  }
}
